
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, KeyRound } from 'lucide-react';
import { Button, DatePicker, Field, toast } from '@/shared/ui';
import { isoToday } from '@/shared/ui/Calendar/calendar-date';
import {
  apiErrorText,
  ticketFoldersApi,
  type FolderGrant,
  type RequestedAccess,
} from '@/shared/api';
import { FOLDER_ACCESS_LABELS, type FolderAccessType } from '@/shared/api/forms';
import styles from './FolderGrantsCard.module.css';

export interface FolderGrantsCardProps {
  ticketId: string;
}

const pairKey = (path: string, subject: { id?: string | null; label?: string | null }) =>
  `${path}|${subject.id ?? `ext:${(subject.label ?? '').trim().toLowerCase()}`}`;

export const FolderGrantsCard: React.FC<FolderGrantsCardProps> = ({ ticketId }) => {
  const [grants, setGrants] = useState<FolderGrant[]>([]);
  const [requested, setRequested] = useState<RequestedAccess[]>([]);
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await ticketFoldersApi.ofTicket(ticketId);
      setGrants(res.data.grants);
      setRequested(res.data.requested);
    } catch {
      setGrants([]);
      setRequested([]);
    }
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const done = useMemo(
    () =>
      new Set(
        grants.map((grant) =>
          pairKey(grant.path, { id: grant.subjectUserId, label: grant.subjectName }),
        ),
      ),
    [grants],
  );

  const pending = useMemo(
    () => requested.filter((item) => item.subject && !done.has(pairKey(item.path, item.subject))),
    [requested, done],
  );

  if (requested.length === 0 && grants.length === 0) return null;

  const issue = async (items: RequestedAccess[]) => {
    const payload = items
      .filter((item) => item.subject)
      .map((item) => ({
        ...(item.subject!.id
          ? { subjectUserId: item.subject!.id }
          : { subjectLabel: item.subject!.label }),
        path: item.path,
        accessType: (item.accessType ?? 'read') as FolderAccessType,
        validUntil: validUntil === '' ? null : new Date(validUntil).toISOString(),
      }));
    if (payload.length === 0) return;

    setBusy(true);
    try {
      await ticketFoldersApi.grantMany(ticketId, payload);
      await load();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отметить выдачу'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <h2 className={styles.title}>Доступы</h2>
        {pending.length > 0 && (
          <Button variant="ghost" onClick={() => void issue(pending)} disabled={busy}>
            <KeyRound size={16} aria-hidden />
            {pending.length > 1 ? `Выдал всё (${pending.length})` : 'Выдал'}
          </Button>
        )}
      </div>

      { }
      {pending.length > 0 && (
        <Field label="Действует до" hint="Необязательно: без срока доступ бессрочный">
          { }
          <DatePicker value={validUntil} onChange={setValidUntil} min={isoToday()} />
        </Field>
      )}

      <ul className={styles.list}>
        {requested.map((item) => {
          const granted = item.subject && done.has(pairKey(item.path, item.subject));
          const grant = granted
            ? grants.find(
                (row) =>
                  row.path === item.path &&
                  pairKey(row.path, { id: row.subjectUserId, label: row.subjectName }) ===
                    pairKey(item.path, item.subject!),
              )
            : undefined;
          return (
            <li
              key={`${item.path}|${item.subject?.id ?? item.subject?.label ?? 'none'}`}
              className={styles.item}
            >
              <span className={styles.path}>{item.path}</span>
              <span className={styles.meta}>
                {FOLDER_ACCESS_LABELS[(item.accessType ?? 'read') as FolderAccessType]} ·{' '}
                {item.subject?.label ?? 'получатель не указан'}
                {item.subject && !item.subject.id && ' (нет учётной записи)'}
                {grant?.validUntil &&
                  ` · до ${new Date(grant.validUntil).toLocaleDateString('ru-RU')}`}
              </span>
              {granted ? (
                <span className={styles.done}>
                  <Check size={14} aria-hidden />
                  выдано
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.issue}
                  onClick={() => void issue([item])}
                  disabled={busy || !item.subject}
                >
                  Выдал
                </button>
              )}
            </li>
          );
        })}

        { }
        {grants
          .filter(
            (grant) =>
              !requested.some(
                (item) =>
                  item.subject &&
                  pairKey(item.path, item.subject) ===
                    pairKey(grant.path, { id: grant.subjectUserId, label: grant.subjectName }),
              ),
          )
          .map((grant) => (
            <li key={grant.id} className={styles.item}>
              <span className={styles.path}>{grant.path}</span>
              <span className={styles.meta}>
                {FOLDER_ACCESS_LABELS[grant.accessType]} · {grant.subjectName ?? '—'} · сверх заявки
              </span>
              <span className={styles.done}>
                <Check size={14} aria-hidden />
                выдано
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
};
